/**
 * Kanaalkoppelingen: de agenda van dit platform en die van Airbnb, Booking.com en
 * consorten gelijk houden.
 *
 * Dit scherm draait om één vraag die de verhuurder elke dag heeft: staat mijn
 * woning ergens dubbel verhuurd? Daarom staat de werkvoorraad — de conflicten —
 * bovenaan en niet onderaan bij de instellingen. Een conflict betekent dat een
 * kanaal een periode heeft verkocht die hier al bezet was; dat is geen technische
 * melding maar een gast die anders voor een dichte deur staat.
 *
 * Vier keuzes die geen opmaak zijn:
 *
 * 1. **De export-URL staat verborgen tot je hem opvraagt.** Wie die URL heeft, ziet
 *    de bezette periodes van een woning zonder in te loggen. Hem standaard op het
 *    scherm zetten betekent dat hij in elke schermafdruk en elke schermdeling
 *    meegaat. Er staat een knop naast om hem ongeldig te maken.
 * 2. **"Nu synchroniseren" toont de uitkomst, ook als die slecht is.** Dat een
 *    kanaal niet antwoordt is informatie, geen storing van het platform; de reden
 *    komt op het scherm.
 * 3. **"Kanaal aanhouden" vraagt om bevestiging.** Die keuze annuleert een echte
 *    reservering van een echte gast. Eén klik is te weinig.
 * 4. **Er staat geen tekst in dit bestand die niet uit de catalogus komt** (§17.8),
 *    op data uit de database na: de naam van een woning en de foutmelding van een
 *    kanaal zijn gegevens, geen interfacetekst.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { useFormatters } from '@siteshowroom/i18n/react'
import type { Formatters } from '@siteshowroom/i18n'

import { useTranslate } from '../lib/i18n-provider'
import type { TranslateFn } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  CHANNELS,
  channelErrorMessageKey,
  createConnection,
  fetchExportPack,
  importNow,
  listConflicts,
  listConnections,
  removeConnection,
  resolveConflict,
  rotateExportToken,
  updateConnection,
} from '../lib/channels-client'
import type {
  Channel,
  ChannelConflict,
  ChannelConnection,
  ChannelFailure,
  ConflictResolution,
  ExportPack,
  ImportOutcome,
  PackImage,
} from '../lib/channels-client'
import { useLocale } from '../lib/i18n-provider'

/**
 * Een verblijfsdatum is een kalenderdatum in de tijdzone van de woning, geen
 * tijdstip. Hij wordt daarom als UTC-middernacht gelezen én in UTC geformatteerd:
 * zou de browser hem in zijn eigen zone tonen, dan verschuift 1 juni in
 * Los Angeles naar 31 mei — een nacht verschil op het scherm. Zelfde reden en
 * zelfde vorm als in `bookings-page.tsx`.
 */
function formatCalendarDate(formatters: Formatters, value: string): string {
  return formatters.formatDate(`${value}T00:00:00Z`, { timeZone: 'UTC' })
}

interface Loaded<T> {
  readonly phase: 'ready'
  readonly value: T
}

type Loading<T> =
  | { readonly phase: 'loading' }
  | { readonly phase: 'failed'; readonly error: ChannelFailure }
  | Loaded<T>

/** Vertaalde melding plus het onvertaalde technische detail ernaast. */
function FailureNotice({ error }: { readonly error: ChannelFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(channelErrorMessageKey(error))}</p>
      {/* Diagnose-informatie, bewust onvertaald — zelfde keuze als elders. */}
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

/** Wanneer er voor het laatst is gesynchroniseerd, en of dat lukte. */
function LastRun({ connection }: { readonly connection: ChannelConnection }): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  if (connection.lastImportAt === null) {
    return <span className="facts__aside">{t('dashboard.channels.sync.never')}</span>
  }

  const moment = formatters.formatDateTime(connection.lastImportAt)
  if (connection.lastImportOk === false) {
    return (
      <span className="facts__aside" data-tone="error">
        {t('dashboard.channels.sync.failed', { moment })}
        {/* De reden komt van het kanaal en is dus data. */}
        {connection.lastError !== '' && (
          <code className="notice__detail">{connection.lastError}</code>
        )}
      </span>
    )
  }
  return <span className="facts__aside">{t('dashboard.channels.sync.ok', { moment })}</span>
}

/** De uitkomst van een handmatige synchronisatie. */
function ImportSummary({ outcome }: { readonly outcome: ImportOutcome }): ReactElement {
  const t = useTranslate()

  if (!outcome.ok) {
    return (
      <div className="notice notice--error" role="status">
        <p className="notice__title">{t('dashboard.channels.import.failed')}</p>
        <code className="notice__detail">{outcome.error}</code>
      </div>
    )
  }

  return (
    <div className="notice" role="status">
      <p className="notice__title">
        {t('dashboard.channels.import.done', {
          seen: outcome.eventsSeen,
          created: outcome.staysCreated,
          removed: outcome.staysRemoved,
        })}
      </p>
      {outcome.conflicts > 0 && (
        <p className="notice__detailText">
          {t('dashboard.channels.import.conflicts', { count: outcome.conflicts })}
        </p>
      )}
      {outcome.skipped > 0 && (
        <p className="notice__detailText">
          {t('dashboard.channels.import.skipped', { count: outcome.skipped })}
        </p>
      )}
    </div>
  )
}

/** Eén koppeling, met de knoppen die erbij horen. */
function ConnectionCard({
  connection,
  busy,
  onChanged,
  onFailure,
}: {
  readonly connection: ChannelConnection
  readonly busy: boolean
  readonly onChanged: () => Promise<void>
  readonly onFailure: (error: ChannelFailure) => void
}): ReactElement {
  const t = useTranslate()
  const [working, setWorking] = useState(false)
  const [showUrl, setShowUrl] = useState(false)
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const disabled = busy || working

  async function run(work: () => Promise<{ ok: boolean; error?: ChannelFailure }>): Promise<void> {
    setWorking(true)
    const result = await work()
    if (!result.ok && result.error !== undefined) {
      onFailure(result.error)
    }
    await onChanged()
    setWorking(false)
  }

  async function handleImport(): Promise<void> {
    setWorking(true)
    const result = await importNow(connection.id)
    if (result.ok) {
      setOutcome(result.data)
    } else {
      onFailure(result.error)
    }
    await onChanged()
    setWorking(false)
  }

  return (
    <article className="request-card">
      <header className="request-card__header">
        <div>
          <h3 className="request-card__title">
            {t(`dashboard.channels.name.${connection.channel}`)}
            {/* Het etiket is door de verhuurder ingevuld en dus data. */}
            {connection.label !== '' && <span className="facts__aside">{connection.label}</span>}
          </h3>
          <LastRun connection={connection} />
        </div>
        <span className="deadline" data-tone={connection.status === 'error' ? 'expired' : 'calm'}>
          {t(`dashboard.channels.status.${connection.status}`)}
        </span>
      </header>

      <dl className="facts">
        <div className="facts__row">
          <dt>{t('dashboard.channels.field.importUrl')}</dt>
          <dd>
            {connection.importUrl === '' ? (
              t('dashboard.channels.importUrl.none')
            ) : (
              // De URL van het kanaal is data en wordt niet vertaald.
              <code className="notice__detail">{connection.importUrl}</code>
            )}
          </dd>
        </div>
        <div className="facts__row">
          <dt>{t('dashboard.channels.field.exportUrl')}</dt>
          <dd>
            {showUrl ? (
              <code className="notice__detail">{connection.exportUrl}</code>
            ) : (
              <span className="facts__aside">{t('dashboard.channels.exportUrl.hidden')}</span>
            )}
            <button
              className="button button--quiet"
              onClick={() => {
                setShowUrl((current) => !current)
              }}
              type="button"
            >
              {showUrl
                ? t('dashboard.channels.exportUrl.hide')
                : t('dashboard.channels.exportUrl.show')}
            </button>
          </dd>
        </div>
      </dl>

      {outcome !== null && <ImportSummary outcome={outcome} />}

      <div className="request-panel__actions">
        <button
          className="button"
          disabled={disabled || connection.importUrl === ''}
          onClick={() => void handleImport()}
          type="button"
        >
          {working ? t('dashboard.channels.action.syncing') : t('dashboard.channels.action.sync')}
        </button>

        <button
          className="button button--quiet"
          disabled={disabled}
          onClick={() =>
            void run(async () =>
              updateConnection(connection.id, {
                status: connection.status === 'paused' ? 'active' : 'paused',
              }),
            )
          }
          type="button"
        >
          {connection.status === 'paused'
            ? t('dashboard.channels.action.resume')
            : t('dashboard.channels.action.pause')}
        </button>

        <button
          className="button button--quiet"
          disabled={disabled}
          onClick={() => void run(async () => rotateExportToken(connection.id))}
          type="button"
        >
          {t('dashboard.channels.action.rotate')}
        </button>

        {confirmingDelete ? (
          <>
            {/* Verwijderen laat de geïmporteerde periodes staan: die datums zijn
                nog steeds verkocht bij het kanaal. Dat hoort er te staan. */}
            <span className="facts__aside">{t('dashboard.channels.remove.warning')}</span>
            <button
              className="button button--danger"
              disabled={disabled}
              onClick={() => void run(async () => removeConnection(connection.id))}
              type="button"
            >
              {t('dashboard.channels.remove.confirm')}
            </button>
            <button
              className="button button--quiet"
              onClick={() => {
                setConfirmingDelete(false)
              }}
              type="button"
            >
              {t('dashboard.channels.action.back')}
            </button>
          </>
        ) : (
          <button
            className="button button--quiet"
            disabled={disabled}
            onClick={() => {
              setConfirmingDelete(true)
            }}
            type="button"
          >
            {t('dashboard.channels.action.remove')}
          </button>
        )}
      </div>
    </article>
  )
}

/** Eén conflict, met de drie manieren om het af te handelen. */
function ConflictCard({
  conflict,
  busy,
  onResolved,
  onFailure,
}: {
  readonly conflict: ChannelConflict
  readonly busy: boolean
  readonly onResolved: () => Promise<void>
  readonly onFailure: (error: ChannelFailure) => void
}): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()
  const [working, setWorking] = useState(false)
  const [confirming, setConfirming] = useState(false)

  async function handle(resolution: ConflictResolution): Promise<void> {
    setWorking(true)
    const result = await resolveConflict(conflict.id, resolution)
    if (!result.ok) {
      onFailure(result.error)
    }
    await onResolved()
    setWorking(false)
  }

  const dates = t('dashboard.channels.conflict.dates', {
    checkIn: formatCalendarDate(formatters, conflict.checkIn),
    checkOut: formatCalendarDate(formatters, conflict.checkOut),
  })

  return (
    <article className="request-card request-card--alert">
      <header className="request-card__header">
        <h3 className="request-card__title">
          {t('dashboard.channels.conflict.title', {
            channel: t(`dashboard.channels.name.${conflict.channel}`),
          })}
        </h3>
        <span className="request-card__reference">{dates}</span>
      </header>

      <p className="prose">{t('dashboard.channels.conflict.body')}</p>

      {confirming ? (
        <div className="request-panel">
          <h4 className="request-panel__title">{t('dashboard.channels.conflict.verify.title')}</h4>
          <p className="request-panel__hint">{t('dashboard.channels.conflict.verify.body')}</p>
          <div className="request-panel__actions">
            <button
              className="button button--danger"
              disabled={busy || working}
              onClick={() => void handle('kept_channel')}
              type="button"
            >
              {t('dashboard.channels.conflict.verify.submit')}
            </button>
            <button
              className="button button--quiet"
              onClick={() => {
                setConfirming(false)
              }}
              type="button"
            >
              {t('dashboard.channels.action.back')}
            </button>
          </div>
        </div>
      ) : (
        <div className="request-panel__actions">
          <button
            className="button"
            disabled={busy || working}
            onClick={() => void handle('kept_local')}
            type="button"
          >
            {t('dashboard.channels.conflict.keepLocal')}
          </button>
          <button
            className="button button--quiet"
            disabled={busy || working || conflict.conflictingStayId === null}
            onClick={() => {
              setConfirming(true)
            }}
            type="button"
          >
            {t('dashboard.channels.conflict.keepChannel')}
          </button>
          <button
            className="button button--quiet"
            disabled={busy || working}
            onClick={() => void handle('manual')}
            type="button"
          >
            {t('dashboard.channels.conflict.manual')}
          </button>
        </div>
      )}
    </article>
  )
}

/** Het formulier om een koppeling toe te voegen. */
function NewConnectionForm({
  residenceId,
  busy,
  onCreated,
  onFailure,
}: {
  readonly residenceId: string
  readonly busy: boolean
  readonly onCreated: () => Promise<void>
  readonly onFailure: (error: ChannelFailure) => void
}): ReactElement {
  const t = useTranslate()
  const [channel, setChannel] = useState<Channel>('airbnb')
  const [label, setLabel] = useState('')
  const [importUrl, setImportUrl] = useState('')
  const [working, setWorking] = useState(false)

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setWorking(true)
    const result = await createConnection({
      residenceId,
      channel,
      label: label.trim(),
      importUrl: importUrl.trim(),
      exportEnabled: true,
    })
    if (result.ok) {
      setLabel('')
      setImportUrl('')
    } else {
      onFailure(result.error)
    }
    await onCreated()
    setWorking(false)
  }

  return (
    <form
      className="request-panel"
      onSubmit={(event) => {
        void handleSubmit(event)
      }}
    >
      <h3 className="request-panel__title">{t('dashboard.channels.add.title')}</h3>
      <p className="request-panel__hint">{t('dashboard.channels.add.intro')}</p>

      <label className="field field--inline">
        <span className="field__label">{t('dashboard.channels.field.channel')}</span>
        <select
          className="field__input"
          disabled={busy || working}
          onChange={(event) => {
            setChannel(event.target.value as Channel)
          }}
          value={channel}
        >
          {CHANNELS.map((option) => (
            <option key={option} value={option}>
              {t(`dashboard.channels.name.${option}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__label">{t('dashboard.channels.field.label')}</span>
        <input
          className="field__input"
          disabled={busy || working}
          maxLength={200}
          onChange={(event) => {
            setLabel(event.target.value)
          }}
          type="text"
          value={label}
        />
        <span className="field__hint">{t('dashboard.channels.field.labelHint')}</span>
      </label>

      <label className="field">
        <span className="field__label">{t('dashboard.channels.field.importUrl')}</span>
        <input
          className="field__input"
          disabled={busy || working}
          maxLength={2000}
          onChange={(event) => {
            setImportUrl(event.target.value)
          }}
          type="url"
          value={importUrl}
        />
        <span className="field__hint">{t('dashboard.channels.field.importUrlHint')}</span>
      </label>

      <div className="request-panel__actions">
        <button className="button" disabled={busy || working} type="submit">
          {working ? t('dashboard.channels.add.submitting') : t('dashboard.channels.add.submit')}
        </button>
      </div>
    </form>
  )
}

/**
 * Het exportpakket: de teksten en foto's klaar om over te zetten.
 *
 * Dit is de tussenstap zolang er geen API-koppeling per kanaal is (ADR-037).
 * Overzetten blijft handwerk; voorbereiden niet.
 *
 * Er wordt niets afgekapt. Staat een omschrijving over de grens van het kanaal,
 * dan staat erbij hoeveel tekens eraf moeten — een half afgekapte zin ziet er
 * precies genoeg uit om ongemerkt te worden gepubliceerd.
 */
function ExportPackPanel({
  residenceId,
  onFailure,
}: {
  readonly residenceId: string
  readonly onFailure: (error: ChannelFailure) => void
}): ReactElement {
  const t = useTranslate()
  const { locale } = useLocale()
  const [channel, setChannel] = useState<Channel>('airbnb')
  const [pack, setPack] = useState<ExportPack | null>(null)
  const [working, setWorking] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  async function load(target: Channel): Promise<void> {
    setWorking(true)
    setPack(null)
    const result = await fetchExportPack(residenceId, target, locale)
    if (result.ok) {
      setPack(result.data)
    } else {
      onFailure(result.error)
    }
    setWorking(false)
  }

  async function copy(field: string, value: string): Promise<void> {
    // `navigator.clipboard` bestaat niet overal en werkt alleen op een beveiligde
    // verbinding. Lukt het niet, dan blijft de tekst gewoon te selecteren staan —
    // dat is geen fout die een melding verdient.
    try {
      await navigator.clipboard.writeText(value)
      setCopied(field)
    } catch {
      setCopied(null)
    }
  }

  return (
    <section className="page__section">
      <h2 className="page__sectionTitle">{t('dashboard.channels.pack.title')}</h2>
      <p className="prose">{t('dashboard.channels.pack.intro')}</p>

      <label className="field field--inline">
        <span className="field__label">{t('dashboard.channels.field.channel')}</span>
        <select
          className="field__input"
          disabled={working}
          onChange={(event) => {
            const next = event.target.value as Channel
            setChannel(next)
            void load(next)
          }}
          value={channel}
        >
          {CHANNELS.map((option) => (
            <option key={option} value={option}>
              {t(`dashboard.channels.name.${option}`)}
            </option>
          ))}
        </select>
        <button
          className="button"
          disabled={working}
          onClick={() => void load(channel)}
          type="button"
        >
          {working ? t('dashboard.channels.pack.loading') : t('dashboard.channels.pack.build')}
        </button>
      </label>

      {pack !== null && (
        <>
          {pack.fields.map((field) => (
            <div className="pack-field" key={field.field}>
              <div className="pack-field__head">
                <span className="field__label">
                  {t(`dashboard.channels.pack.field.${field.field}`)}
                </span>
                <span className="pack-field__count" data-tone={field.excess > 0 ? 'error' : 'calm'}>
                  {field.excess > 0
                    ? t('dashboard.channels.pack.tooLong', {
                        length: field.length,
                        limit: field.limit,
                        excess: field.excess,
                      })
                    : t('dashboard.channels.pack.fits', {
                        length: field.length,
                        limit: field.limit,
                      })}
                </span>
              </div>
              {/* De tekst is content van de verhuurder en wordt niet vertaald. */}
              <textarea
                className="field__input field__input--tall"
                readOnly
                rows={3}
                value={field.value}
              />
              <button
                className="button button--quiet"
                onClick={() => void copy(field.field, field.value)}
                type="button"
              >
                {copied === field.field
                  ? t('dashboard.channels.pack.copied')
                  : t('dashboard.channels.pack.copy')}
              </button>
            </div>
          ))}

          <h3 className="page__sectionTitle">
            {t('dashboard.channels.pack.photos', { count: pack.images.length })}
          </h3>
          {pack.imagesOmitted > 0 && (
            <p className="prose">
              {t('dashboard.channels.pack.omitted', {
                count: pack.imagesOmitted,
                max: pack.maxImages,
              })}
            </p>
          )}
          <ol className="pack-photos">
            {pack.images.map((image) => (
              <li className="pack-photos__item" key={image.assetId}>
                <a href={image.url} rel="noreferrer" target="_blank">
                  <img alt={image.altText} className="pack-photos__thumb" src={image.url} />
                </a>
                <span className="pack-photos__meta">
                  {/* De kamer staat vóór het formaat: bij het overzetten is dat de
                      vraag die als eerste beantwoord moet worden. Kanalen vragen per
                      foto om een kamertype (§17.14). */}
                  <span className="pack-photos__space">{packSpaceName(image, t)}</span>
                  {t('dashboard.channels.pack.size', {
                    width: image.width,
                    height: image.height,
                  })}
                  {image.tooSmall && (
                    <span className="pack-photos__warning">
                      {t('dashboard.channels.pack.tooSmall', { min: pack.minImageWidth })}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>

          {/* De bron van de grenzen; zonder die vermelding is niet te zien
              waarom een tekst te lang zou zijn. */}
          <p className="prose">
            {t('dashboard.channels.pack.source', { source: pack.limitSource })}
          </p>
        </>
      )}
    </section>
  )
}

function residenceName(residences: readonly ResidenceOption[], id: string): string {
  return residences.find((residence) => residence.id === id)?.name ?? ''
}

export function ChannelsPage(): ReactElement {
  const t: TranslateFn = useTranslate()

  const [residences, setResidences] = useState<Loading<readonly ResidenceOption[]>>({
    phase: 'loading',
  })
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [connections, setConnections] = useState<Loading<readonly ChannelConnection[]>>({
    phase: 'loading',
  })
  const [conflicts, setConflicts] = useState<readonly ChannelConflict[]>([])
  const [failure, setFailure] = useState<ChannelFailure | null>(null)
  const [busy, setBusy] = useState(false)

  const connectionsController = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    const result = await listResidences()
    if (!result.ok) {
      setResidences({ phase: 'failed', error: result.error })
      return
    }
    setResidences({ phase: 'ready', value: result.data })
    setResidenceId((current) => current ?? result.data[0]?.id ?? null)
  }, [])

  const loadConnections = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    connectionsController.current?.abort()
    const controller = new AbortController()
    connectionsController.current = controller

    const [list, open] = await Promise.all([
      listConnections(residenceId, { signal: controller.signal }),
      listConflicts({ signal: controller.signal }),
    ])
    if (controller.signal.aborted) {
      return
    }

    if (!list.ok) {
      // Afgebroken is geen fout: dat gebeurt bij elke wissel van residence.
      if (list.error.kind !== 'aborted') {
        setConnections({ phase: 'failed', error: list.error })
      }
      return
    }
    setConnections({ phase: 'ready', value: list.data })
    setConflicts(open.ok ? open.data : [])
  }, [residenceId])

  useEffect(() => {
    // Zie de toelichting op hetzelfde patroon in media-page.tsx en
    // bookings-page.tsx: state wordt pas ná de await gezet, de begintoestand is al
    // 'loading', en de cleanup breekt de aanvraag af. De lintregel kan dat
    // statisch niet zien.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
  }, [loadResidences])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadConnections()
    return () => {
      connectionsController.current?.abort()
    }
  }, [loadConnections])

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true)
    await loadConnections()
    setBusy(false)
  }, [loadConnections])

  const openConflicts = conflicts.filter(
    (conflict) => conflict.resolvedAt === null && conflict.residenceId === residenceId,
  )

  return (
    <section className="page">
      <h1 className="page__title">{t('dashboard.channels.title')}</h1>
      <p className="prose">{t('dashboard.channels.intro')}</p>

      {failure !== null && <FailureNotice error={failure} />}

      {residences.phase === 'failed' && <FailureNotice error={residences.error} />}

      {residences.phase === 'ready' && residences.value.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.channels.noResidence')}
        </p>
      )}

      {residences.phase === 'ready' && residences.value.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.channels.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.value.map((residence) => (
              // De naam komt uit de database en wordt niet vertaald.
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* De werkvoorraad staat bovenaan: een conflict is een dubbele verhuur. */}
      {openConflicts.length > 0 && (
        <section className="page__section">
          <h2 className="page__sectionTitle">
            {t('dashboard.channels.conflicts.title', { count: openConflicts.length })}
          </h2>
          <p className="prose">{t('dashboard.channels.conflicts.intro')}</p>
          {openConflicts.map((conflict) => (
            <ConflictCard
              busy={busy}
              conflict={conflict}
              key={conflict.id}
              onFailure={setFailure}
              onResolved={refresh}
            />
          ))}
        </section>
      )}

      {residenceId !== null && (
        <section className="page__section">
          <h2 className="page__sectionTitle">
            {t('dashboard.channels.connections.title', {
              residence: residenceName(
                residences.phase === 'ready' ? residences.value : [],
                residenceId,
              ),
            })}
          </h2>

          {connections.phase === 'loading' && (
            <p className="page__status" role="status">
              {t('dashboard.channels.loading')}
            </p>
          )}
          {connections.phase === 'failed' && <FailureNotice error={connections.error} />}
          {connections.phase === 'ready' && connections.value.length === 0 && (
            <p className="page__status" role="status">
              {t('dashboard.channels.connections.empty')}
            </p>
          )}
          {connections.phase === 'ready' &&
            connections.value.map((connection) => (
              <ConnectionCard
                busy={busy}
                connection={connection}
                key={connection.id}
                onChanged={refresh}
                onFailure={setFailure}
              />
            ))}

          <NewConnectionForm
            busy={busy}
            onCreated={refresh}
            onFailure={setFailure}
            residenceId={residenceId}
          />

          <ExportPackPanel onFailure={setFailure} residenceId={residenceId} />
        </section>
      )}
    </section>
  )
}

/**
 * De kamer bij een foto in het exportpakket.
 *
 * Bewust niet `spaceLabel()` uit `spaces-client.ts`: die kijkt naar de hele lijst om
 * te bepalen of het volgnummer erbij hoort, en het pakket kent alleen de kamer van
 * déze foto. Hier staat het nummer er dus altijd bij vanaf twee — bij het overzetten
 * naar een kanaal is een nummer te veel minder erg dan een verwisselde badkamer.
 */
function packSpaceName(image: PackImage, t: TranslateFn): string {
  if (image.space === null) {
    return t('dashboard.channels.pack.noSpace')
  }
  if (image.space.label !== '') {
    return image.space.label
  }
  const kindName = t(`dashboard.spaces.kind.${image.space.kind}`)
  return image.space.ordinal > 1 ? `${kindName} ${String(image.space.ordinal)}` : kindName
}
